import { useEcranAide, useDemandeEcran } from './aide/AideContexte';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { departementCourant, peutModifier } from '../services/droits';
import { stageAPI } from '../services/api';
import { dateFr } from '../services/dates';
import PhotoEtudiant from './PhotoEtudiant';
import { teinteAvatar, initiales } from '../services/etudiantsAffichage';
import StageModale from './StageModale';
import StageChoixTuteur from './StageChoixTuteur';
import StageModaleEnvoi from './StageModaleEnvoi';
import { AccordeonMail } from './StageParametrageContacts';
import StageRepartition, { tonCharge } from './StageRepartition';
import StageParametrageCfa from './StageParametrageCfa';
import StageTransmissionCfa from './StageTransmissionCfa';
import { AvatarEnseignant } from './PhotoEnseignant';
import { COURRIELS_TUTEURS as COURRIELS } from '../services/modelesCourrielPresentation';

/**
 * ONGLET TUTEURS (lot 3.5a) — spec suivi_de_stage.md E138 → E148, maquette `#tab-tuteurs`.
 *
 *   Tableau de bord tuteurs : 4 cartes · bloc « Étudiants sans tuteur » AU-DESSUS (stagiaires ET
 *   alternants, il disparaît quand chacun a un tuteur) · la répartition par enseignant (une colonne
 *   par processus avec visite + Alternants) · le volet d'un tuteur (ses étudiants).
 *   Paramétrage → Relance et courriels des tuteurs : le délai de relance du département (E141).
 *
 * ⚠️ Une seule donnée par étudiant (E142) : affecter ici, dans le tableau de bord stagiaire ou dans
 * le panneau ⑧ écrit le MÊME champ. Arrivent ensuite : les courriels (3.5a-3), l'aide à la
 * répartition (3.5b), la transmission au CFA (3.5d).
 */
// Lot L1a (E169) : « d'abord les paramètres, puis le tableau de bord » (Bertrand) — l'ordre et les numéros ;
// la vignette s'ouvre toujours sur le tableau de bord.
const ECRANS = [
    { cle: 'param', libelle: '1 · Paramètres',      icone: 'fa-gear' },
    { cle: 'dash',  libelle: '2 · Tableau de bord', icone: 'fa-chart-bar' }
];
const LECTURE_SEULE = 'Lecture seule : votre profil ne permet pas de modifier ce module.';
const nom = (e) => (e ? `${e.nom} ${e.prenom || ''}`.trim() : '');
const lieu = (l) => [l.entreprise_cp, l.entreprise_ville].filter(Boolean).join(' ');
const message = (e, defaut) => e.response?.data?.detail || e.response?.data?.error || defaut;

function Avatar({ l }) {
    return (
        <PhotoEtudiant chemin={l.photo_url} className="stg-avatar stg-avatar-photo" alt=""
            secours={<div className="stg-avatar" style={{ background: teinteAvatar(l.nom, l.prenom) }}>{initiales(l.nom, l.prenom)}</div>} />
    );
}

/** Un étudiant : bande bleue (stagiaire FI) ou violette (alternant), comme la maquette. */
function Formation({ l }) {
    return <span className={'stg-form' + (l.type === 'alternant' ? ' alt' : '')}>{l.formation}</span>;
}

function StageTuteurs({ config, user, anneeId, onOuvrirProcessus }) {
    const [ecran, setEcran] = useState('dash');
    // Aide contextuelle (A1) : l'aide à la répartition (page pleine) fait partie du tableau de bord des tuteurs.
    useEcranAide('tuteurs/' + (ecran === 'param' ? 'param' : 'tableau'));
    useDemandeEcran('tuteurs', (s) => { if (s === 'tableau') setEcran('dash'); else if (s === 'param') setEcran('param'); });
    const departementId = departementCourant(user, config);
    const modifiable = peutModifier(user, departementId, 'stage');

    const [donnees, setDonnees] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [enCours, setEnCours] = useState(false);
    const [recherche, setRecherche] = useState('');
    const [colonne, setColonne] = useState('');
    const [population, setPopulation] = useState('');
    const [choixLigne, setChoixLigne] = useState({});      // clé étudiant → enseignant choisi (bloc sans tuteur)
    const [volet, setVolet] = useState(null);              // id de l'enseignant dont le volet est ouvert
    const [changer, setChanger] = useState(null);          // l'étudiant dont on change le tuteur (modale)
    const [succes, setSucces] = useState(null);
    const [coches, setCoches] = useState({});              // volet : étudiants cochés (clé → bool)
    const [envoi, setEnvoi] = useState(null);              // brouillon ou mailto ouvert (fenêtre d'envoi)

    const recharger = useCallback(async () => {
        if (!departementId) return;
        try {
            const r = await stageAPI.getTuteurs(departementId, anneeId);
            setDonnees(r.data);
            setErreur(null);
        } catch (e) { setErreur(message(e, 'Lecture des tuteurs impossible')); }
    }, [departementId, anneeId]);
    useEffect(() => { recharger(); }, [recharger]);

    const cle = (l) => `${l.type}:${l.dossier_id || l.etudiant_id}`;
    const affecter = async (l, tuteurId, apres) => {
        setEnCours(true); setErreur(null);
        try {
            if (l.type === 'alternant') await stageAPI.affecterTuteurAlternant(l.etudiant_id, departementId, anneeId, tuteurId);
            else await stageAPI.affecterTuteur(l.dossier_id, departementId, tuteurId);
            await recharger();
            if (apres) apres();
        } catch (e) { setErreur(message(e, "L'affectation n'a pas abouti")); }
        finally { setEnCours(false); }
    };

    /** Lot 3.5a-3 : préparer « Information tuteur » (plusieurs étudiants) ou « Information du suivi » (un). */
    const cible = (l) => (l.type === 'alternant' ? { type: 'alternant', etudiant_id: l.etudiant_id } : { type: 'stagiaire', dossier_id: l.dossier_id });
    // Lot MC : la dernière demande, pour refaire le brouillon après « Modèles de courriel… »
    const derniereDemande = useRef(null);
    const preparer = async (demande) => {
        derniereDemande.current = demande;
        setEnCours(true); setErreur(null);
        try {
            const r = await stageAPI.preparerEnvoiTuteurs(departementId, anneeId, demande);
            setEnvoi(r.data);
        } catch (e) { setErreur(message(e, "Le courriel n'a pas pu être préparé")); }
        finally { setEnCours(false); }
    };

    // ── filtres : processus (colonne) et population. La charge affichée reste TOTALE (E143).
    const garde = useCallback((l) => (!colonne || l.colonne === colonne)
        && (!population || (population === 'alternants' ? l.type === 'alternant' : l.type === 'stagiaire')), [colonne, population]);
    const colonnes = useMemo(() => (donnees?.colonnes || []).filter((c) =>
        (!colonne || c.cle === colonne) && (!population || (population === 'alternants' ? c.cle === 'alternants' : c.cle !== 'alternants'))),
    [donnees, colonne, population]);
    const sansTuteur = (donnees?.sans_tuteur || []).filter(garde);
    const enseignants = useMemo(() => {
        const q = recherche.trim().toLowerCase();
        return (donnees?.enseignants || [])
            .filter((e) => e.actif || e.charge > 0)
            .filter((e) => !q || nom(e).toLowerCase().includes(q))
            .sort((a, b) => b.charge - a.charge || a.nom.localeCompare(b.nom, 'fr'));
    }, [donnees, recherche]);

    if (!departementId) {
        return <div className="stg-encart stg-encart-alerte"><i className="fa-solid fa-triangle-exclamation" /><span>Aucun département sélectionné.</span></div>;
    }

    const entete = (
        <div className="certif-entete">
            <div className="param-subtab-bar" role="tablist" aria-label="Écrans des tuteurs">
                {ECRANS.map((e) => (
                    <button key={e.cle} type="button" role="tab" aria-selected={ecran === e.cle}
                            className={'param-subtab' + (ecran === e.cle ? ' active' : '')} onClick={() => setEcran(e.cle)}>
                        <i className={'fa-solid ' + e.icone} /> {e.libelle}
                    </button>
                ))}
            </div>
        </div>
    );

    // Lot 3.5b-2 : l'aide à la répartition est une PAGE PLEINE (sa barre a « Retour aux tuteurs »).
    if (ecran === 'repartition' || ecran === 'repartition-generation') {
        return (
            <StageRepartition departementId={departementId} anneeId={anneeId} modifiable={modifiable}
                              ouvrirGeneration={ecran === 'repartition-generation'}
                              onRetour={() => { setEcran('dash'); recharger(); }} />
        );
    }

    if (ecran === 'param') {
        return (
            <div>
                {entete}
                <ParametrageTuteurs departementId={departementId} anneeId={anneeId} modifiable={modifiable} delai={donnees?.delai_relance_sem}
                                    quiSuit={donnees?.qui_suit} onOuvrirProcessus={onOuvrirProcessus}
                                    onSucces={setSucces}
                                    onEnregistre={(n) => { setDonnees((d) => (d ? { ...d, delai_relance_sem: n } : d)); setSucces(`Délai de relance enregistré : ${n} semaine${n > 1 ? 's' : ''}.`); }}
                                    onReglages={(r) => { setDonnees((d) => (d ? { ...d, portail_cfa: r.portail_cfa } : d));
                                                         setSucces(r.portail_cfa ? `Adresse du portail du CFA enregistrée : ${r.portail_cfa}` : 'Adresse du portail du CFA effacée.'); }} />
                {succes && <StageModale titre="Paramétrage enregistré" icone="fa-circle-check" ton="succes" onFermer={() => setSucces(null)}><p>{succes}</p></StageModale>}
            </div>
        );
    }

    if (!donnees) {
        return (
            <div>
                {entete}
                {erreur ? <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>
                        : <div className="stg-attente"><i className="fa-solid fa-circle-notch stg-tourne" /><div>Lecture des tuteurs…</div></div>}
            </div>
        );
    }

    /**
     * Le clic sur la carte « Étudiants sans tuteur » mène au bloc du même nom, juste dessous (défilement doux).
     * ⚠️ CONTOURNEMENT (2026-10-13, choix 2 de Bertrand) : depuis l'aide contextuelle, le défilement doux ne partait
     * pas environ 1 fois sur 4 ou 5 (banc 35a ; 0 sur 9 sans l'aide ; cause non trouvée, le défaut disparaît dès
     * qu'on l'observe — SUIVI_DEV, étape A4). Si le bloc n'a pas bougé 400 ms après le clic et n'est pas déjà en haut,
     * on y va sans animation. La recherche de la cause reste inscrite au suivi.
     */
    const allerAuBlocSansTuteur = () => {
        const bloc = document.getElementById('stg-bloc-sans-tuteur');
        if (!bloc) return;
        const avant = bloc.getBoundingClientRect().top;
        bloc.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => {
            const apres = bloc.getBoundingClientRect().top;
            if (Math.abs(apres - avant) < 2 && apres > 5) bloc.scrollIntoView({ block: 'start' });
        }, 400);
    };

    // Les 4 cartes de la maquette (#tab-tuteurs, .stats-row / .stat-card) : chiffre en gros dans la couleur de la
    // carte, libellé gris sur une ligne avec sa précision entre parenthèses.
    const c = donnees.cartes;
    const pc = c.plus_charge;
    const tonMax = pc ? tonCharge(pc.charge, pc.souhaite ?? 0) : null;
    const cartes = [
        { cle: 'affectes', couleur: '#2563eb', valeur: c.tuteurs_affectes, sur: c.enseignants_disponibles,
          picto: '👨‍🏫', libelle: 'Tuteurs affectés', precision: 'enseignants disponibles',
          titre: 'Enseignants qui suivent au moins un étudiant, sur les enseignants de l’année actifs et disponibles (aide à la répartition)' },
        { cle: 'sans', couleur: c.sans_tuteur ? '#d97706' : '#16a34a', valeur: c.sans_tuteur, picto: '⚠️', libelle: 'Étudiants sans tuteur',
          // Choix de Bertrand (2026-10-10) : le clic mène au bloc « Étudiants sans tuteur » (stagiaires ET alternants), juste dessous.
          clic: c.sans_tuteur ? allerAuBlocSansTuteur : null,
          titre: c.sans_tuteur ? 'Aller à la liste des étudiants sans tuteur, pour les affecter' : 'Chaque étudiant a un tuteur' },
        { cle: 'moyenne', couleur: '#16a34a', valeur: String(c.charge_moyenne).replace('.', ','), picto: '📊', libelle: 'Charge moyenne', precision: 'étudiants / tuteur',
          titre: 'Étudiants par tuteur affecté' },
        { cle: 'max', couleur: undefined, petit: true, picto: '🔴', libelle: 'Tuteur le plus chargé', precision: 'charge / souhaité',
          valeur: pc ? <>{pc.nom}{pc.prenom ? ` ${pc.prenom[0].toUpperCase()}.` : ''} <span style={{ color: tonMax.couleur }}>{pc.charge} / {pc.souhaite ?? 0}</span></> : '—',
          titre: pc ? `${nom(pc)} : ${pc.charge} étudiant${pc.charge > 1 ? 's' : ''} suivi${pc.charge > 1 ? 's' : ''} pour ${pc.souhaite ?? 0} souhaité${(pc.souhaite ?? 0) > 1 ? 's' : ''}` : undefined }
    ];
    const ouvert = volet ? (donnees.enseignants || []).find((e) => e.id === volet) : null;
    const ses = ouvert ? (donnees.etudiants || []).filter((l) => l.tuteur_id === ouvert.id) : [];

    return (
        <div>
            {entete}
            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}

            <div className="stg-stats">
                {cartes.map((k) => (
                    <div key={k.cle} className={`stg-stat${k.clic ? ' stg-stat-clic' : ''}`} title={k.titre} data-carte={k.cle}
                         onClick={k.clic || undefined} role={k.clic ? 'button' : undefined} tabIndex={k.clic ? 0 : undefined}
                         onKeyDown={k.clic ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); k.clic(); } } : undefined}>
                        <div className={`stg-stat-val${k.petit ? ' stg-stat-val-petit' : ''}`} style={{ color: k.couleur }}>
                            {k.valeur}{k.sur !== undefined && <span className="stg-stat-sur"> / {k.sur}</span>}
                        </div>
                        <div className="stg-stat-lib">{k.picto} {k.libelle}{k.precision && <span className="stg-stat-precision"> ({k.precision})</span>}</div>
                    </div>
                ))}
            </div>

            <div className="stg-filtres">
                <div className="stg-recherche">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input placeholder="Rechercher un tuteur…" value={recherche} onChange={(e) => setRecherche(e.target.value)} aria-label="Rechercher un tuteur" />
                </div>
                <select className="input-sm stg-filtre" value={colonne} onChange={(e) => setColonne(e.target.value)} aria-label="Processus" title="Un processus = une formation × un semestre">
                    <option value="">Tous les processus avec visite</option>
                    {(donnees.colonnes || []).filter((x) => x.cle !== 'alternants').map((x) => <option key={x.cle} value={x.cle}>{x.libelle}</option>)}
                </select>
                <select className="input-sm stg-filtre" value={population} onChange={(e) => setPopulation(e.target.value)} aria-label="Population">
                    <option value="">Stagiaires et alternants</option>
                    <option value="stagiaires">Stagiaires seulement</option>
                    <option value="alternants">Alternants seulement</option>
                </select>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    {/* Lot 3.5d-3 (E148, E149) → L3b-2 : seulement si le CFA suit au moins une formation d'apprentis. */}
                    {donnees.transmission_cfa && (
                        <StageTransmissionCfa departementId={departementId} anneeId={anneeId} modifiable={modifiable} lectureSeule={LECTURE_SEULE}
                                              onTermine={() => recharger()} onParametrage={() => setEcran('param')} />
                    )}
                    <button type="button" className="btn btn-green btn-sm" onClick={() => setEcran('repartition')}
                            title="Répartir les étudiants entre les tuteurs : glisser-déposer, génération automatique, versions">
                        <i className="fa-solid fa-wand-magic-sparkles" /> Aide à la répartition
                    </button>
                </div>
            </div>

            {/* ── Étudiants sans tuteur : AU-DESSUS, disparaît quand chacun en a un (E139) ── */}
            {sansTuteur.length > 0 && (
                <div className="card stg-sans-tuteur" id="stg-bloc-sans-tuteur">
                    <div className="card-header">
                        <span><i className="fa-solid fa-triangle-exclamation" /> Étudiants sans tuteur <span className="badge badge-wait">{sansTuteur.length}</span></span>
                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>Ce bloc disparaît quand chacun a un tuteur</span>
                    </div>
                    <div className="stg-table-defile">
                        <table>
                            <thead><tr><th>Étudiant</th><th>Formation</th><th>Entreprise</th><th style={{ width: 330 }}>Tuteur pédagogique</th></tr></thead>
                            <tbody>
                                {sansTuteur.map((l) => (
                                    <tr key={cle(l)} className={'stg-st-ligne' + (l.type === 'alternant' ? ' alt' : '')}>
                                        <td><div className="stg-identite"><Avatar l={l} /><div><strong>{l.nom}</strong> {l.prenom}</div></div></td>
                                        <td><Formation l={l} /></td>
                                        <td style={l.entreprise_nom ? undefined : { color: '#94a3b8', fontStyle: 'italic' }}>
                                            {l.entreprise_nom || 'Entreprise pas encore connue'}{lieu(l) && <span style={{ fontSize: 11, color: '#64748b' }}> · {lieu(l)}</span>}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <select className="input-sm" style={{ flex: 1 }} disabled={!modifiable || enCours} aria-label={`Tuteur de ${l.nom} ${l.prenom}`}
                                                        value={choixLigne[cle(l)] || ''} onChange={(e) => setChoixLigne((p) => ({ ...p, [cle(l)]: e.target.value }))}>
                                                    <option value="">— Choisir —</option>
                                                    {(donnees.enseignants || []).filter((e) => e.actif).sort((a, b) => a.charge - b.charge || a.nom.localeCompare(b.nom, 'fr'))
                                                        .map((e) => <option key={e.id} value={e.id}>{nom(e)} ({e.charge})</option>)}
                                                </select>
                                                <button type="button" className="btn btn-green btn-sm" disabled={!modifiable || enCours || !choixLigne[cle(l)]}
                                                        title={modifiable ? undefined : LECTURE_SEULE}
                                                        onClick={() => affecter(l, Number(choixLigne[cle(l)]), () => setChoixLigne((p) => ({ ...p, [cle(l)]: '' })))}>
                                                    <i className="fa-solid fa-check" /> Affecter
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── La répartition par enseignant ── */}
            <div className="card">
                <div className="card-header">
                    <span><i className="fa-solid fa-chalkboard-user" /> Répartition des tuteurs{donnees.annee?.libelle ? ` — ${donnees.annee.libelle}` : ''}</span>
                    {donnees.processus_sans_visite?.length > 0 && (
                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>
                            Non compté (processus sans visite) : {donnees.processus_sans_visite.join(', ')}
                        </span>
                    )}
                </div>
                <div className="stg-table-defile">
                    <table>
                        <thead>
                            <tr>
                                <th>Tuteur</th>
                                <th style={{ width: 170 }}>Charge <span style={{ fontWeight: 400, color: '#64748b' }}>/ souhaité</span></th>
                                {colonnes.map((x) => <th key={x.cle} style={{ textAlign: 'center' }}>{x.libelle}</th>)}
                                <th style={{ textAlign: 'center' }}>Total</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {enseignants.length === 0 && (
                                <tr><td colSpan={colonnes.length + 4} style={{ textAlign: 'center', color: '#64748b', padding: '24px 14px' }}>
                                    {recherche ? 'Aucun enseignant ne correspond à cette recherche.'
                                        : "Aucun enseignant pour cette année dans ce département : ajoutez-les dans le module Enseignants."}
                                </td></tr>
                            )}
                            {enseignants.map((e) => (
                                <tr key={e.id} className="stg-ligne" style={e.actif ? undefined : { opacity: 0.6 }} onClick={() => { setVolet(e.id); setCoches({}); }} title="Voir ses étudiants">
                                    <td>
                                        <div className="stg-tut-ident">
                                            <AvatarEnseignant e={e} className="stg-tut-av" />
                                            <div>
                                                <strong>{nom(e)}</strong>
                                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                                    {e.statut || 'Enseignant'}{!e.actif && ' · plus actif cette année'}{e.visites_en_retard > 0 && <span style={{ color: '#d97706' }}> · {e.visites_en_retard} visite{e.visites_en_retard > 1 ? 's' : ''} en retard</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        {/* E140 : la barre et le chiffre se lisent contre le nombre SOUHAITÉ de l'enseignant. */}
                                        {(() => {
                                            const s = e.souhaite ?? 0; const ton = tonCharge(e.charge, s);
                                            const largeur = s > 0 ? Math.min(100, (e.charge / s) * 100) : (e.charge ? 100 : 0);
                                            return (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <span className="stg-tut-barre large"><span style={{ width: `${largeur}%`, background: ton.barre }} /></span>
                                                    <span style={{ fontSize: 11, fontWeight: 700, color: e.charge || s ? ton.couleur : '#94a3b8', whiteSpace: 'nowrap' }}
                                                          title={e.disponible === false ? 'Non disponible pour de nouveaux suivis' : undefined}>
                                                        {e.charge} / {s}{ton.depasse ? ' ⚠️' : ''}
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                    </td>
                                    {colonnes.map((x) => (
                                        <td key={x.cle} style={{ textAlign: 'center', color: e.par_colonne[x.cle] ? undefined : '#94a3b8' }}>{e.par_colonne[x.cle] || 0}</td>
                                    ))}
                                    <td style={{ textAlign: 'center' }}><strong style={{ color: e.charge > (e.souhaite ?? 0) ? '#dc2626' : undefined }}>{e.charge}</strong></td>
                                    <td className="actions" onClick={(ev) => ev.stopPropagation()}>
                                        <button type="button" className="action-btn" title="Voir ses étudiants" onClick={() => { setVolet(e.id); setCoches({}); }}><i className="fa-solid fa-eye" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="stg-tut-legende">
                    <span><span className="carre" style={{ background: '#16a34a' }} />sous le nombre souhaité</span>
                    <span><span className="carre" style={{ background: '#f59e0b' }} />nombre souhaité atteint</span>
                    <span><span className="carre" style={{ background: '#dc2626' }} />au-delà (surchargé)</span>
                    <span style={{ marginLeft: 'auto' }}>Le nombre souhaité se règle dans{' '}
                        <button type="button" onClick={() => setEcran(modifiable ? 'repartition-generation' : 'repartition')}>l'aide à la répartition</button></span>
                </div>
            </div>

            {ouvert && (
                <div className="stg-panneau-fond" onClick={() => setVolet(null)}>
                    <div className="stg-panneau certif-panneau" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Étudiants du tuteur">
                        <div className="stg-panneau-corps">
                            <div className="stg-panneau-entete">
                                <AvatarEnseignant e={ouvert} className="stg-tut-av stg-tut-av-grand" />
                                <div style={{ flex: 1 }}>
                                    <h3>{nom(ouvert)}</h3>
                                    <div className="stg-panneau-sous">{ouvert.statut || 'Enseignant'}{ouvert.email ? ` · ${ouvert.email}` : ''} · {ouvert.charge} étudiant{ouvert.charge > 1 ? 's' : ''} suivi{ouvert.charge > 1 ? 's' : ''}</div>
                                </div>
                                <button type="button" className="action-btn" onClick={() => setVolet(null)} title="Fermer (Échap)"><i className="fa-solid fa-xmark" /></button>
                            </div>
                            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}
                            {ses.length === 0 && <div className="stg-section-avenir"><i className="fa-solid fa-circle-minus" /> Aucun étudiant ne lui est affecté.</div>}
                            {ses.length > 0 && (
                                <label className="stg-case" style={{ marginBottom: 6 }}>
                                    <input type="checkbox" checked={ses.every((l) => coches[cle(l)])}
                                           onChange={(ev) => setCoches(Object.fromEntries(ses.map((l) => [cle(l), ev.target.checked])))} /> Tout cocher
                                </label>
                            )}
                            {ses.map((l) => (
                                <div key={cle(l)} className={'stg-tv-ligne' + (l.type === 'alternant' ? ' alt' : '')}>
                                    <input type="checkbox" checked={!!coches[cle(l)]} aria-label={`Cocher ${l.nom} ${l.prenom}`}
                                           onChange={(ev) => setCoches((p) => ({ ...p, [cle(l)]: ev.target.checked }))} />
                                    <Avatar l={l} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div><strong>{l.nom}</strong> {l.prenom} <Formation l={l} /></div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>{l.entreprise_nom || 'Entreprise pas encore connue'}{lieu(l) ? ` · ${lieu(l)}` : ''}</div>
                                        <div style={{ fontSize: 11, marginTop: 2 }}>
                                            <span style={{ color: l.prevenu_tuteur_le ? '#16a34a' : '#d97706' }}>
                                                {l.prevenu_tuteur_le ? `Tuteur prévenu le ${dateFr(l.prevenu_tuteur_le)}` : 'Tuteur pas encore prévenu'}
                                            </span>
                                            {l.prevenu_maitre_le && <span style={{ color: '#16a34a' }}> · Maître prévenu le {dateFr(l.prevenu_maitre_le)}</span>}
                                        </div>
                                    </div>
                                    <div className="stg-tv-etat">
                                        {l.type === 'alternant' && l.suivi_cfa
                                                ? <span style={{ color: '#7c3aed' }}><i className="fa-solid fa-handshake" /> Suivi par le CFA</span>
                                            : l.type === 'alternant' && !l.avec_visite
                                                ? <span style={{ color: '#64748b' }}>{l.suivi_par ? `Suivi dans « ${l.suivi_par} »` : "Pas de fiche de visite dans l'application"}</span>
                                            : l.visite_faite ? <span style={{ color: '#16a34a' }}><i className="fa-solid fa-circle-check" /> Visite faite</span>
                                            : l.echeance_visite ? <span style={{ color: l.en_retard ? '#d97706' : '#64748b' }}>{l.en_retard && <i className="fa-solid fa-clock" />} Visite avant le {dateFr(l.echeance_visite)}</span>
                                            : <span style={{ color: '#64748b' }}>Affecté{l.tuteur_affecte_le ? ` le ${dateFr(l.tuteur_affecte_le)}` : ''}</span>}
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            {!(l.type === 'alternant' && l.suivi_cfa) && <button type="button" className="action-btn" disabled={!modifiable || enCours}
                                                    title={modifiable ? 'Prévenir son maître de stage (Information du suivi)' : LECTURE_SEULE}
                                                    onClick={() => preparer({ genre: 'maitre', cibles: [cible(l)] })}><i className="fa-solid fa-briefcase" /></button>}
                                            {!l.visite_faite && (
                                                <button type="button" className="btn btn-outline btn-sm" disabled={!modifiable || enCours} title={modifiable ? 'Changer ou retirer le tuteur' : LECTURE_SEULE}
                                                        onClick={() => setChanger(l)}><i className="fa-solid fa-user-pen" /> Changer</button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {ses.length > 0 && (
                                <div className="stg-doc-cloture" style={{ marginTop: 10 }}>
                                    <button type="button" className="btn btn-green btn-sm" disabled={!modifiable || enCours || !ses.some((l) => coches[cle(l)])}
                                            title={modifiable ? 'Un seul message au tuteur, qui liste les étudiants cochés' : LECTURE_SEULE}
                                            onClick={() => preparer({ genre: 'tuteur', tuteurId: ouvert.id, cibles: ses.filter((l) => coches[cle(l)]).map(cible) })}>
                                        <i className="fa-solid fa-envelope" /> Prévenir le tuteur ({ses.filter((l) => coches[cle(l)]).length})
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {envoi && (
                <StageModaleEnvoi envoi={envoi} departementId={departementId} onFermer={() => setEnvoi(null)} onTermine={() => recharger()}
                    rePreparer={() => stageAPI.preparerEnvoiTuteurs(departementId, anneeId, derniereDemande.current).then((r) => r.data)} />
            )}

            {changer && (
                <StageModale titre={`Tuteur de ${changer.prenom} ${changer.nom}`} icone="fa-chalkboard-user" enCours={enCours} onFermer={() => setChanger(null)}>
                    <StageChoixTuteur enseignants={donnees.enseignants} actuelId={changer.tuteur_id} enCours={enCours}
                                      lectureSeule={modifiable ? null : LECTURE_SEULE}
                                      onAffecter={(id) => affecter(changer, id, () => setChanger(null))} />
                </StageModale>
            )}
        </div>
    );
}

// Lot MC (2026-10-28) : la description vit dans services/modelesCourrielPresentation.js (lue aussi par les fenêtres d'envoi).

/** Paramétrage → CFA (qui suit les apprentis ; transmission au CFA, lot 3.5d-2) et Relance et courriels des tuteurs. */
function ParametrageTuteurs({ departementId, anneeId, modifiable, delai, quiSuit, onOuvrirProcessus, onEnregistre, onReglages, onSucces }) {
    const [onglet, setOnglet] = useState('cfa');
    // Aide contextuelle (A1)
    useEcranAide('tuteurs/param/' + onglet);
    useDemandeEcran('tuteurs/param', (s) => { if (s === 'cfa' || s === 'relance') setOnglet(s); });

    const [portail, setPortail] = useState('');
    const [lu, setLu] = useState(false);              // les réglages réels sont arrivés (pas d'affichage du défaut avant)
    const [valeur, setValeur] = useState(delai || 4);
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur] = useState(null);
    const [modeles, setModeles] = useState(null);
    const [ouvert, setOuvert] = useState(null);
    useEffect(() => { if (delai) setValeur(delai); }, [delai]);
    useEffect(() => {
        let vivant = true;
        stageAPI.getParametrageTuteurs(departementId)
            .then((r) => { if (vivant) { setModeles(r.data.modeles_mail || []); if (r.data.delai_relance_sem) setValeur(r.data.delai_relance_sem); setPortail(r.data.portail_cfa || ''); setLu(true); } })
            .catch((e) => { if (vivant) setErreur(message(e, 'Lecture des courriels impossible')); });
        return () => { vivant = false; };
    }, [departementId]);
    const enregistrer = async () => {
        setEnCours(true); setErreur(null);
        try {
            const r = await stageAPI.enregistrerReglagesTuteurs(departementId, { delaiRelanceSem: Number(valeur) });
            onEnregistre(r.data.delai_relance_sem);
        } catch (e) { setErreur(message(e, "L'enregistrement n'a pas abouti")); }
        finally { setEnCours(false); }
    };
    // L3b-2 (E172) : « qui suit » se déduit des processus ; seul le portail du CFA s'enregistre ici.
    const cfaSuitUneFormation = (quiSuit || []).some((f) => !f.processus.length);
    const enregistrerPortail = async () => {
        setEnCours(true); setErreur(null);
        try {
            const r = await stageAPI.enregistrerReglagesTuteurs(departementId, { portailCfa: portail });
            if (onReglages) onReglages(r.data);
        } catch (e) { setErreur(message(e, "L'enregistrement n'a pas abouti")); }
        finally { setEnCours(false); }
    };
    return (
        <div>
            <div className="param-subtab-bar" role="tablist" aria-label="Paramétrage des tuteurs" style={{ marginBottom: 12 }}>
                <button type="button" role="tab" aria-selected={onglet === 'cfa'} className={'param-subtab' + (onglet === 'cfa' ? ' active' : '')} onClick={() => setOnglet('cfa')}>
                    <i className="fa-solid fa-paper-plane" /> CFA
                </button>
                <button type="button" role="tab" aria-selected={onglet === 'relance'} className={'param-subtab' + (onglet === 'relance' ? ' active' : '')} onClick={() => setOnglet('relance')}>
                    <i className="fa-solid fa-chalkboard-user" /> Relance et courriels des tuteurs
                </button>
            </div>
            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}
            {onglet === 'cfa' && !lu && !erreur && <div className="stg-section-avenir"><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture du réglage…</div>}
            {onglet === 'cfa' && lu && (
                <div className="card stg-suivi-apprentis" id="stg-qui-suit" style={{ marginBottom: 14, borderColor: '#ddd6fe' }}>
                    <div className="card-header" style={{ background: '#f5f3ff' }}>
                        <span><i className="fa-solid fa-handshake" style={{ color: '#7c3aed' }} /> Qui suit les apprentis ?</span>
                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>Déduit des processus de suivi de l'année — rien à régler ici</span>
                    </div>
                    <div style={{ padding: '12px 16px', fontSize: 12 }}>
                        {!quiSuit ? <div className="stg-section-avenir"><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture…</div>
                            : !quiSuit.length ? <div style={{ color: '#64748b' }}>Aucun apprenti inscrit cette année.</div>
                            : (
                                <div className="stg-table-defile" style={{ maxWidth: 820 }}><table>
                                    <thead><tr><th>Formation</th><th>Apprentis</th><th>Suivis par</th></tr></thead>
                                    <tbody>
                                        {quiSuit.map((f) => (
                                            <tr key={f.sous_niveau_id || 0} data-formation={f.formation}>
                                                <td><b>{f.formation}</b></td>
                                                <td>{f.nb_apprentis}</td>
                                                <td>
                                                    {f.processus.length
                                                        ? <><span className="pf-chip ok">Département Manager</span>{' '}
                                                            {f.processus.map((p, i) => (
                                                                <React.Fragment key={p.id}>{i > 0 && ', '}
                                                                    <button type="button" className="stg-lien-bouton" style={{ fontSize: 11, fontWeight: 400 }} disabled={!onOuvrirProcessus}
                                                                            onClick={() => onOuvrirProcessus && onOuvrirProcessus(p.id)}>dans « {p.titre} »</button>
                                                                </React.Fragment>
                                                            ))}</>
                                                        : <><span className="pf-chip">le CFA</span>{' '}<span style={{ color: '#64748b', fontSize: 11 }}>aucun processus ne la coche</span></>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table></div>
                            )}
                        <div style={{ margin: '10px 0 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            Adresse du portail du CFA :
                            <input className="input-sm" style={{ width: 280 }} value={portail} placeholder="https://www.cfa-cloe.fr" disabled={!modifiable || enCours}
                                   onChange={(e) => setPortail(e.target.value)} aria-label="Adresse du portail du CFA" />
                            <span style={{ color: '#64748b', fontSize: 11 }}>— donnée au tuteur dans « Information tuteur », pour les apprentis suivis par le CFA</span>
                            <button type="button" className="btn btn-green btn-sm" style={{ marginLeft: 'auto' }} disabled={!modifiable || enCours}
                                    title={modifiable ? undefined : LECTURE_SEULE} onClick={enregistrerPortail}>
                                <i className="fa-solid fa-check" /> Enregistrer
                            </button>
                        </div>
                        <div style={{ marginTop: 8, color: '#64748b', fontSize: 11 }}>
                            <i className="fa-solid fa-circle-info" /> Pour qu'une formation soit suivie ici : la cocher dans un processus de suivi
                            (« Le processus » › Formations suivies). La transmission au CFA ci-dessous ne concerne que les formations suivies par le CFA.
                        </div>
                    </div>
                </div>
            )}
            {/* Lot 3.5d-2 (E148, E165) → L3b-2 : la transmission au CFA — dès que le CFA suit au moins une formation (#cfaTransmission). */}
            {onglet === 'cfa' && lu && cfaSuitUneFormation && (
                <StageParametrageCfa departementId={departementId} anneeId={anneeId} modifiable={modifiable} onSucces={onSucces} />
            )}
            {onglet === 'cfa' && lu && quiSuit && quiSuit.length > 0 && !cfaSuitUneFormation && (
                <div className="stg-encart" id="stg-pas-de-transmission"><i className="fa-solid fa-circle-info" />
                    <span>Toutes les formations d'apprentis sont suivies dans Département Manager : il n'y a pas de transmission au CFA cette année.</span></div>
            )}
            {onglet === 'relance' && (<>
            <div className="card" style={{ marginBottom: 14 }}>
                <div className="card-header">
                    <span><i className="fa-solid fa-bell" /> Relance du tuteur</span>
                    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>Réglage du département, pour tous les processus avec visite</span>
                </div>
                <div style={{ padding: '14px 16px', fontSize: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        La visite est attendue au plus tard
                        <input type="number" min={1} max={26} className="input-sm" style={{ width: 64 }} value={valeur} disabled={!modifiable || enCours}
                               onChange={(e) => setValeur(e.target.value)} aria-label="Délai de relance en semaines" />
                        semaines après l'affectation du tuteur, et jamais après la fin du stage.
                        <button type="button" className="btn btn-green btn-sm" style={{ marginLeft: 'auto' }} disabled={!modifiable || enCours}
                                title={modifiable ? undefined : LECTURE_SEULE} onClick={enregistrer}>
                            <i className="fa-solid fa-check" /> Enregistrer
                        </button>
                    </div>
                    <div style={{ marginTop: 8, color: '#64748b', fontSize: 11 }}>
                        Passé ce délai, la ligne passe en orange pâle dans le tableau de bord et le bouton devient « Relancer le tuteur ».
                        Les alternants ne sont pas concernés (pas de fiche de visite dans l'application).
                    </div>
                </div>
            </div>
            <div className="card">
                <div className="card-header">
                    <span><i className="fa-solid fa-envelope" /> Courriels des tuteurs</span>
                    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>Même éditeur et mêmes versions que les courriels des stagiaires</span>
                </div>
                <div style={{ padding: '12px 16px' }}>
                    {!modeles ? <div className="stg-section-avenir"><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture des courriels…</div>
                        : modeles.map((m) => (
                            <AccordeonMail key={m.type_mail} modele={m} ouvert={ouvert === m.type_mail}
                                onBasculer={() => setOuvert((o) => (o === m.type_mail ? null : m.type_mail))}
                                departementId={departementId} modifiable={modifiable}
                                onMiseAJour={(r) => { if (r?.modeles_mail) setModeles(r.modeles_mail); }}
                                {...(COURRIELS[m.type_mail] || {})} />
                        ))}
                </div>
            </div>
            </>)}
        </div>
    );
}

export default StageTuteurs;
